import './quantityInput.scss';

import { useField, useFormikContext } from 'formik';
import {
  ClipboardEvent,
  DragEvent,
  KeyboardEvent,
  SyntheticEvent,
  useCallback,
  useEffect,
  useState,
} from 'react';
import { Button, FormControl, InputGroup } from 'react-bootstrap';
import { FaMinus, FaPlus, FaRegTrashAlt } from 'react-icons/fa';
import { toast } from 'react-toastify';

import { useOrderContext } from '@/resources/hooks/useOrderContext';
import { isInRange } from '@/resources/themes/product/default/utils/comboUtils';

import {
  canChangeOrder,
  canUseWebappInStatuses,
  delay,
  removeMask,
} from '../../../../services/global';
import { ICartProduct, IOrder } from '../../../../store/ducks/order/types';
import { IProduct } from '../../../../store/ducks/products/types';
import { useGetPage } from '../../../hooks/useGetPage';
import { useModal, useModalReturn } from '../../../hooks/useModal';
import { useWebContext } from '../../../hooks/useWebContext';
import { formatToBestCombo } from '../../../themes/product/default/utils/comboUtils';
import DeleteItem from './DeleteItem';

const getQuantityFormated = (quantity: number, current_measure: string, seeAsUnity: boolean) => {
  const pattern = /([\d]+)\.([\d]+)/;
  const isQuantityRight = new RegExp(pattern);
  let newQuantity = quantity;

  if (!seeAsUnity && current_measure === 'kg' && !isQuantityRight.test(quantity.toString())) {
    newQuantity = parseFloat(quantity.toString().replace(/[^\d]/g, ''));
  }

  return newQuantity;
};

const returnQuantityComma = (quantity: number, current_measure: string) =>
  current_measure === 'kg' ? `${quantity.toFixed(3).replace('.', ',')} KG` : quantity;

export const quantityReceive = (value: string, currentMeasure: string, seeAsUnity: boolean) => {
  let newValue = value;
  newValue = value === '' ? '0' : value;

  const parsedValue = parseFloat(newValue.toString().replace(',', '.'));
  const quantity: number = getQuantityFormated(parsedValue, currentMeasure, seeAsUnity);

  return quantity <= 0 ? 0 : returnQuantityComma(quantity, currentMeasure);
};

interface Props {
  type: string;
  name: string;
  fullWidth?: boolean;
  currentMeasure: string;
  automaticSubmit?: boolean;
  product: IProduct | ICartProduct;
  direction?: 'center' | 'end';
  optionModal: useModalReturn;
}

function QuantityInput({
  type,
  name,
  currentMeasure,
  product,
  fullWidth = false,
  automaticSubmit = false,
  direction = 'center',
  optionModal,
}: Props) {
  const { order } = useWebContext();
  const isCartPage = useGetPage('cart');
  const { submitForm, isSubmitting } = useFormikContext();
  const [valid, setValid] = useState<boolean>(false);
  const [verifyBtnClicked, setVerifyBtnClicked] = useState<boolean>(false);
  const [field, { error, touched, value }, { setValue }] = useField({ type, name });
  const { useFunction } = useOrderContext();

  const isCheckout = useGetPage('checkout');

  const modal = useModal();

  const getCartItem = useCallback(
    (customOrder?: IOrder) => {
      if (!product || !order?.cart.products) return null;

      const useOrder = customOrder || order;

      const cartItem = useOrder.cart.products.find((cartItem) => {
        if (product.uid) {
          return cartItem.uid === product.uid;
        }

        const basicMatch =
          cartItem.product_id === product.product_id && cartItem.ean === product.ean;

        if ((product as ICartProduct).combo_seq_id) {
          return basicMatch && cartItem.combo_seq_id === (product as ICartProduct).combo_seq_id;
        }

        return basicMatch && !cartItem.combo_seq_id;
      });

      return cartItem ?? null;
    },
    [product, order],
  );

  const cartItem = getCartItem();

  const addOrderQuantity = product.options_group?.options.some(
    (option) => option.add_order_quantity,
  );

  const convertKgToUnity = (value: string | number) => {
    const floatQuantity = parseFloat(
      value.toString().toLowerCase().replace(' kg', '').replace(',', '.'),
    );

    return product.see_as_unit
      ? floatQuantity.toString()
      : (floatQuantity / product.average_weight).toFixed(0);
  };
  const [unity, setUnity] = useState<string>(
    product.see_as_unit ? (cartItem?.quantity ?? product.quantity).toString() : '',
  );

  const handleClick = (value: number, type: string, currentMeasure: string) => {
    const parsedValue = parseFloat(value.toString().replace(',', '.'));
    const quantity: number = getQuantityFormated(parsedValue, currentMeasure, product.see_as_unit);

    let newQuantity = quantity;

    const getQuantityToCalc = () => {
      return 1;
    };

    const quantityToCalc: number = getQuantityToCalc();

    switch (type) {
      case 'add':
        if (addOrderQuantity) return quantity;
        newQuantity = +(quantity + quantityToCalc).toFixed(currentMeasure === 'unity' ? 1 : 3);
        break;
      case 'subtract':
        if (addOrderQuantity) return quantity;
        newQuantity = +(quantity - quantityToCalc).toFixed(currentMeasure === 'unity' ? 1 : 3);
        break;
      default:
        newQuantity = 0;
    }

    const valueToSet = newQuantity < 0 ? 0 : returnQuantityComma(newQuantity, currentMeasure);

    setUnity(convertKgToUnity(valueToSet));

    return valueToSet;
  };

  const getFloatQuantity = (unity: string) => parseFloat(removeMask(unity).trim());
  const convertUnityToKg = (unity: string): string => {
    const quantity = getFloatQuantity(unity === '' ? '0' : unity);

    return (quantity * product.average_weight).toString();
  };

  const handleComboEdit = () => {
    if (!(product as ICartProduct)?.combo_seq_id) return;

    const cartProduct = product as ICartProduct;

    const comboProducts = order?.cart.products.filter(
      (p) => p.combo_seq_id === cartProduct.combo_seq_id,
    );

    if (!comboProducts?.length) return;

    const setEditProducts = useFunction('setEditProducts');
    const setProductToCombo = useFunction('setProductToCombo');
    const openComboDetails = useFunction('openComboDetails');

    if (setEditProducts) {
      setEditProducts(comboProducts);
    }

    const comboFromCart = order?.cart.combos?.find((combo) => combo.id === cartProduct.combo_id);

    if (setProductToCombo && comboFromCart) {
      const bestCombo = formatToBestCombo(comboFromCart, cartProduct);
      setProductToCombo({ best_combo: bestCombo });
    }

    if (openComboDetails) {
      openComboDetails();
    }
  };

  const handleSubmit = (isDelete?: boolean) => {
    setVerifyBtnClicked(true);

    if (addOrderQuantity && !isDelete) return optionModal.toggle();

    if (automaticSubmit && !isSubmitting) {
      submitForm().then((res: any) => {
        const cartProduct = getCartItem(res);

        if (!isDelete || cartProduct) {
          const cartQuantity = cartProduct?.quantity ?? 0;

          const valueToSet = product.see_as_unit
            ? convertKgToUnity(cartQuantity)
            : returnQuantityComma(cartQuantity, currentMeasure);

          setValue(valueToSet);
        }

        setVerifyBtnClicked(false);

        return true;
      });
    }
    return true;
  };

  const handleChangeQuantity = (action: 'add' | 'subtract') => {
    const valueToSet = product.see_as_unit ? unity : value;

    const { isValid } = canChangeOrder({
      type: 'item',
      action: 'alterado',
    });

    if (!isValid) return false;

    const hasPbmPromotion = cartItem?.price_type === 'pbm_price';

    if (hasPbmPromotion && cartItem) {
      const setShowPbmModal = useFunction('setShowPbmModal');
      setShowPbmModal?.(true);
      return false;
    }

    if ((product as ICartProduct)?.combo_seq_id) {
      if (action === 'add') {
        if (
          !isInRange({
            combos: order?.cart.combos ?? [],
            id: product.id,
            combo_id: product.combo_id as number,
            quantity: product.quantity,
            action,
          })
        ) {
          const setModalAddProduct = useFunction('setModalAddProduct');
          if (setModalAddProduct) {
            setModalAddProduct(product as IProduct);
            return null;
          }
        }
      }
      if (action === 'subtract') {
        if (
          !isInRange({
            combos: order?.cart.combos ?? [],
            id: product.id,
            combo_id: product.combo_id as number,
            quantity: product.quantity,
            action,
          })
        ) {
          const setModalRemoveProduct = useFunction('setModalRemoveProduct');
          if (setModalRemoveProduct) {
            setModalRemoveProduct(product as IProduct);
            return null;
          }
        }
      }
    }

    if (addOrderQuantity) return optionModal.toggle();

    setValue(handleClick(valueToSet, action, currentMeasure));

    delay(() => {
      handleSubmit();
    }, 500);

    return true;
  };

  const handleChangeQuantityForm = () => {
    const valueToSet = product.see_as_unit ? unity : value;

    const { isValid } = canChangeOrder({
      type: 'item',
      action: 'alterado',
    });

    if (!isValid) return false;

    if (addOrderQuantity) return optionModal.toggle();

    setValue(quantityReceive(valueToSet, currentMeasure, product.see_as_unit));

    handleSubmit();

    return true;
  };

  useEffect(() => {
    if (product.see_as_unit) {
      setUnity(convertKgToUnity(value));
    }
  }, [value]);

  useEffect(() => {
    if (!isSubmitting && modal.show) modal.onHide();
  }, [isSubmitting]);

  // Set correct initial value from cart item
  useEffect(() => {
    if (cartItem && cartItem.quantity !== undefined) {
      const correctValue = product.see_as_unit
        ? convertKgToUnity(cartItem.quantity)
        : returnQuantityComma(cartItem.quantity, currentMeasure);

      if (value !== correctValue) {
        setValue(correctValue);
      }
    }
  }, [cartItem?.quantity, product.product_id, product.combo_seq_id, product.ean]);

  return (
    <>
      {order &&
        !order.is_paid &&
        !order.integration_code &&
        canUseWebappInStatuses({ status: order.status }) && (
          <>
            <div className={`d-flex justify-content-${direction}`}>
              <div
                className="d-flex justify-content-center flex-column m-0"
                style={{ maxWidth: fullWidth ? '100%' : '135px' }}
              >
                {currentMeasure === 'kg' && product.see_as_unit && (
                  <div className="quantity-tip">
                    Aprox. {returnQuantityComma(+convertUnityToKg(unity), currentMeasure)}
                  </div>
                )}

                <InputGroup
                  className="quantity-input"
                  style={{ width: fullWidth ? 'auto' : 'fit-content' }}
                >
                  <InputGroup.Prepend>
                    <Button
                      className="rounded-left"
                      aria-label="Diminuir quantidade"
                      disabled={verifyBtnClicked || isSubmitting || value <= 0 || isCheckout}
                      onClick={(e: SyntheticEvent) => {
                        e.preventDefault();
                        e.stopPropagation();

                        handleChangeQuantity('subtract');
                      }}
                    >
                      <FaMinus />
                    </Button>
                  </InputGroup.Prepend>

                  {product.see_as_unit && (
                    <FormControl
                      draggable={false}
                      type="text"
                      name="quantity_as_unit"
                      aria-label="Alterar quantidade UNIDADE"
                      value={unity}
                      disabled={verifyBtnClicked || isSubmitting || addOrderQuantity || isCheckout}
                      onBlur={() => {
                        if ((product as ICartProduct)?.combo_seq_id) {
                          return;
                        }
                        setUnity(unity);

                        handleChangeQuantityForm();
                      }}
                      onChange={(e) => {
                        const { value } = e.currentTarget;
                        const formattedValue = value.replace(/[^\d]/g, '');

                        setUnity(formattedValue);
                      }}
                      onClick={(e: SyntheticEvent<HTMLInputElement>) => {
                        e.preventDefault();
                        e.stopPropagation();
                        if ((product as ICartProduct)?.combo_seq_id) {
                          handleComboEdit();
                        }
                      }}
                      onKeyDown={(e: KeyboardEvent<HTMLInputElement>) => {
                        if (e.key === 'Enter') e.preventDefault();
                      }}
                      onDrop={(e: DragEvent<HTMLInputElement>) => e.preventDefault()}
                      onPaste={(e: ClipboardEvent<HTMLInputElement>) => e.preventDefault()}
                    />
                  )}

                  <FormControl
                    {...field}
                    draggable={false}
                    autoComplete="off"
                    value={value}
                    aria-label="Alterar quantidade KG"
                    disabled={verifyBtnClicked || isSubmitting || addOrderQuantity || isCheckout}
                    className={product.see_as_unit ? 'd-none' : ''}
                    onClick={(e: SyntheticEvent<HTMLInputElement>) => {
                      e.preventDefault();
                      e.stopPropagation();
                      if ((product as ICartProduct)?.combo_seq_id) {
                        handleComboEdit();
                      }
                    }}
                    onBlur={() => {
                      if ((product as ICartProduct)?.combo_seq_id) {
                        return null;
                      }

                      const isNotNumber = Number.isNaN(
                        parseFloat(value.toString().replace(',', '.')),
                      );

                      // eslint-disable-next-line consistent-return
                      if (isNotNumber) return toast('Insira um valor válido', { type: 'warning' });

                      if (!product.see_as_unit) {
                        setValue(quantityReceive(value, currentMeasure, product.see_as_unit));

                        handleChangeQuantityForm();
                      }

                      return null;
                    }}
                    onKeyDown={(e: KeyboardEvent<HTMLInputElement>) => {
                      if (e.key === 'Enter') e.preventDefault();
                    }}
                    onDrop={(e: DragEvent<HTMLInputElement>) => e.preventDefault()}
                    onPaste={(e: ClipboardEvent<HTMLInputElement>) => e.preventDefault()}
                  />

                  {error && touched && <div>{error}</div>}

                  <InputGroup.Append>
                    <Button
                      aria-label="Aumentar quantidade"
                      disabled={verifyBtnClicked || isSubmitting || isCheckout}
                      onClick={(e: SyntheticEvent) => {
                        e.preventDefault();
                        e.stopPropagation();

                        handleChangeQuantity('add');
                      }}
                    >
                      <FaPlus />
                    </Button>
                  </InputGroup.Append>
                </InputGroup>
              </div>
              {!isCheckout && (
                <button
                  className={
                    'btn text-danger ml-2 p-0 justify-content-center' +
                    `${product.see_as_unit ? ' align-items-end' : ' align-items-center'}` +
                    `${isCartPage ? ' d-md-flex' : ' d-none d-md-flex'}`
                  }
                  disabled={verifyBtnClicked || isSubmitting}
                  onClick={(e: SyntheticEvent) => {
                    e.preventDefault();
                    e.stopPropagation();

                    const { isValid } = canChangeOrder({
                      type: 'item',
                      action: 'removido',
                    });

                    if (isValid) {
                      setValid(true);
                      modal.toggle();
                    }
                  }}
                >
                  <FaRegTrashAlt />
                </button>
              )}
            </div>

            <div
              onClick={(e: SyntheticEvent) => {
                e.preventDefault();
                e.stopPropagation();
              }}
            >
              <DeleteItem
                modal={modal}
                isValid={valid}
                value={value}
                currentMeasure={currentMeasure}
                setValue={setValue}
                handleClick={handleClick}
                handleSubmit={handleSubmit}
              />
            </div>
          </>
        )}
    </>
  );
}

export default QuantityInput;
